import { isFastLoadingEnabled } from './useFastLoadingStore';
import hasTitleNode from '../helpers/hasTitleNode.helper';
import { usesFormFields } from '../helpers/formFields.helper';
import type { TDocument } from '../types';

/**
 * How big a document has to be before part loading is offered, in stored html characters.
 *
 * Characters, although parts are cut by node count: this is a coarse gate answering "could this
 * document be heavy", not a budget, and it runs before the html has been split into elements.
 *
 * Below it the whole document parses in less time than a single part swap costs, so loading in parts
 * would only add swapping. This exists for imported clinical reports, which are an order of magnitude
 * bigger than an ordinary 50-page document.
 */
export const LARGE_DOCUMENT_MIN_CHARS = 300_000;

/**
 * Which documents load in parts.
 *
 * Editors built without a title node are excluded: reusable content, the mock spreadsheet and a Change
 * Template request. They are small by nature. Form-field editors load whole too: a card has no static
 * drawing, and dragging one across parts is not supported.
 */
export const isPartLoadable = (doc: TDocument): boolean => hasTitleNode(doc) && !usesFormFields(doc);

/**
 * Whether a document is big enough for part loading to be worth offering. Kind is not considered -
 * this answers "is it heavy", `isPartLoadable` answers "is it eligible".
 */
export const isHeavyEnoughForParts = (html: string | undefined): boolean =>
  (html?.length ?? 0) >= LARGE_DOCUMENT_MIN_CHARS;

/**
 * The whole decision: an eligible document, big enough to benefit, and a user who asked for it.
 *
 * The user's switch is deliberately the last word. Nothing changes for anyone who has not turned it
 * on, whatever they open.
 */
export const shouldLoadInParts = (doc: TDocument, html: string): boolean =>
  isPartLoadable(doc) && isHeavyEnoughForParts(html) && isFastLoadingEnabled();
